/* eslint-disable react/jsx-fragments */
import React, { Component, Fragment } from 'react';
import { connect } from 'react-redux';
import {
    Field, reduxForm, formValueSelector, SubmissionError,
} from 'redux-form';
import { FormattedMessage, injectIntl } from 'react-intl';

import Layout from '../layout';
import BaseCheckoutLayout from './base-checkout-layout';
import AddressFieldsConnector from '../../containers/checkout-steps/address-fields-connector';
import CheckoutStepCalculator from '../../services/checkout-step-calculator';
import FormField from './shared/form-field';
import Loader from '../shared/loader';

import validators from '../shared/validators';
import APP_DEFAULTS from '../../constants/app-defaults';
import ZoneService from '../../services/zone-service';
import APP_ROUTES from '../../constants/app-routes';

class AddressForm extends Component {
    constructor(props) {
        super(props);

        document.title = this.props.intl.formatMessage({ id: 'checkout', defaultMessage: 'Checkout' });
    }

    /* Render this step only if order is present and in a valid checkout state. */
    componentWillMount() {
        const { order } = this.props;

        if (!CheckoutStepCalculator.isStepEditable(order.checkout_steps, 'address', order.state) && order.state !== 'cart') {
            this.props.handleCheckoutStepNotEditable(order, this.props.placedOrder);
        }

        if (!order.id) {
            this.props.redirectToCart();
        }
    }

    componentDidMount() {
        this.props.setCurrentCheckoutStep();
    }

    componentDidUpdate(prevProps) {
        if (this.props.locale !== prevProps.locale) {
            document.title = this.props.intl.formatMessage({ id: 'checkout', defaultMessage: 'Checkout' });
        }
    }

    handleAddressFormSubmit(formData) {
        window.scrollTo(0, 0);
        return this.props.handleAddressFormSubmit(formData, this.props.order, this.props.locale.currentLocale).then(
            () => {
            },
            (error) => {
                const sanitizedErrors = this._sanitizedErrorMessages(error.response.body.errors);
                throw new SubmissionError({ order: sanitizedErrors });
            },
        );
    }

    // eslint-disable-next-line class-methods-use-this
    _sanitizedErrorMessages(errors) {
        const { unflatten } = require('flat');

        const unflattenedErrors = unflatten(errors);

        unflattenedErrors.bill_address_attributes = unflattenedErrors.bill_address;
        unflattenedErrors.ship_address_attributes = unflattenedErrors.ship_address;

        return unflattenedErrors;
    }

    render() {
        const {
            handleSubmit, valid, submitting, change,
        } = this.props;
        return (
            <Layout>
                <BaseCheckoutLayout
                    hasExtraTimeCheckout={this.props.hasExtraTimeCheckout}
                    gracePeriod={this.props.gracePeriod}
                    locale={this.props.locale.currentLocale}
                    currentStep="address"
                    checkoutSteps={this.props.order.checkout_steps || []}
                    order={this.props.order}
                >
                    {(submitting || this.props.fetchData.isFetching) && (
                        <div className="checkout-loading-container">
                            <FormattedMessage id="saving-addresses" defaultMessage="Saving addresses" />
                            <Loader displayLoader size={36} />
                        </div>
                    )}
                    {!this.props.fetchData.isFetching && (
                        <form onSubmit={handleSubmit(this.handleAddressFormSubmit.bind(this))} style={{ display: submitting ? 'none' : 'inherit' }}>
                            <div className="checkout-section-title">
                                <i className="pe-7s-user icon" />
                                <div className="checkout-section-title__text-container">
                                    <FormattedMessage id="yourInfo" defaultMessage="Your Information" />
                                    <small className="checkout-section-title__text"><FormattedMessage id="yourInfoDetails" defaultMessage="infoDetails" /></small>
                                </div>
                            </div>

                            <Field
                                className="form-control checkout-form-row"
                                name="order[email]"
                                component={FormField.inputFieldMarkup}
                                label={this.props.intl.formatMessage({ id: 'email', defaultMessage: 'Email' })}
                                placeholder={this.props.intl.formatMessage({ id: 'email-order-placeholder', defaultMessage: 'Email to receive the order confirmation' })}
                                type="text"
                                validate={[validators.required, validators.email]}
                            />

                            <Field
                                name="order[receive_offer]"
                                component={FormField.checkboxInputFieldMarkup}
                                type="checkbox"
                                extraClassName="pb-5"
                                label={this.props.intl.formatMessage({ id: 'offer-keep-update', defaultMessage: 'Keep me updated with news and exclusive offers' })}
                            />

                            <Field
                                name="order[accepts_affidavit_certification]"
                                component={FormField.checkboxInputFieldMarkup}
                                type="checkbox"
                                extraClassName="pt-5"
                                label={(
                                    <Fragment>
                                        {this.props.intl.formatMessage({ id: 'read-and-accept', defaultMessage: 'I have read and I accept the' })}
                                        {' '}
                                        <a href={APP_ROUTES.affidavitCertification} target="_blank">Affidavit Certification</a>
                                    </Fragment>
                                )}
                                validate={[validators.required]}
                            />

                            <div className="checkout-section-title">
                                <i className="pe-7s-note2 icon" />
                                <div className="checkout-section-title__text-container">
                                    <FormattedMessage id="billingInfo" defaultMessage="Billing Info" />
                                    <small className="checkout-section-title__text"><FormattedMessage id="billingInfoDetails" defaultMessage="billingDetails" /></small>
                                </div>
                            </div>

                            {this.props.billCountryIso && this.props.billStateName && (
                                <AddressFieldsConnector
                                    fieldNamePrefix="order[bill_address_attributes]"
                                    selectedCountryIso={this.props.billCountryIso}
                                    selectedStateCode={this.props.billStateName}
                                    changeForm={change}
                                />
                            )}

                            {!ZoneService.isDonationZone() && (
                                <div className="checkout-section-title">
                                    <i className="pe-7s-note2 icon" />
                                    <div className="checkout-section-title__text-container">
                                        <FormattedMessage id="shippingInfo" defaultMessage="Shipping Info" />
                                        <small className="checkout-section-title__text"><FormattedMessage id="shippingInfoDetails" defaultMessage="shippingDetails" /></small>
                                    </div>
                                </div>
                            )}

                            {this.props.shipCountryIso && this.props.shipStateName && !ZoneService.isDonationZone() && (
                                <Fragment>
                                    <Field
                                        className="form-control checkout-form-row"
                                        name="order[email_receiver]"
                                        component={FormField.inputFieldMarkup}
                                        label={this.props.intl.formatMessage({ id: 'email', defaultMessage: 'Email*' })}
                                        placeholder={this.props.intl.formatMessage({ id: 'shippingInfoEmail', defaultMessage: 'Correo electrónico del receptor' })}
                                        type="text"
                                        validate={[validators.email, validators.required]}
                                    />
                                    <div className="receptor-email-legend">
                                        <FormattedMessage
                                            id="emailReceptor"
                                            defaultMessage="* Enviaremos también a este email las notificaciones de cambios de estado de su orden"
                                        />
                                    </div>
                                    <AddressFieldsConnector
                                        fieldNamePrefix="order[ship_address_attributes]"
                                        selectedCountryIso={this.props.shipCountryIso}
                                        selectedStateCode={this.props.shipStateName}
                                        shippingStatesIds={this.props.shippingStatesIds}
                                        selectedCity={this.props.shippingCity}
                                        currentZone={this.props.currentZone}
                                        changeForm={change}
                                    />
                                </Fragment>
                            )}

                            {/* <div className="checkout-form-row checkbox-row">
                                <Field
                                    className="form-control"
                                    name="order[save_user_address]"
                                    component={FormField.inputFieldMarkup}
                                    label={this.props.intl.formatMessage({ id: 'rememberAddress', defaultMessage: 'Remember this Address' })}
                                    type="checkbox"
                                />
                            </div> */}

                            <div className="checkout-form-row navigate">
                                <button type="submit" disabled={!valid || submitting} className={`button--outlined ${submitting && 'button-disabled'}`}>
                                    <FormattedMessage id="continue" defaultMessage="Continue" />
                                </button>
                            </div>
                        </form>
                    )}
                </BaseCheckoutLayout>
            </Layout>
        );
    }
}

AddressForm = reduxForm({
    form: 'addressForm',
})(AddressForm);

const selector = formValueSelector('addressForm');

AddressForm = connect((state) => {
    const isCurrentZoneCuba = ZoneService.isCurrentZoneCuba();

    const defaultBillCountryIso = 'US';
    const defaultBillStateName = 'FL';

    const defaultShippingCountryIso = isCurrentZoneCuba ? 'CU' : 'US';
    const defaultShippingStateCode = isCurrentZoneCuba ? 'HAB' : 'AL';
    // const defaultShippingCity = 'Arroyo Naranjo';
    const defaultShippingCity = state.order.municipalityInfo ? state.order.municipalityInfo.attributes.name : '';

    const defaultShippingZipCode = isCurrentZoneCuba ? '10900' : '';

    const shippingCountriesIds = ['51'];
    const shippingStatesIds = ['422'];

    const billCountryIso = selector(state, 'order[bill_address_attributes][country_iso]');
    const billStateName = selector(state, 'order[bill_address_attributes][state_name]');

    const shipCountryIso = selector(state, 'order[ship_address_attributes][country_iso]');
    const shipStateName = selector(state, 'order[ship_address_attributes][state_name]');
    const shippingCity = selector(state, 'order[ship_address_attributes][city]');

    const billAddress = state.order.bill_address || {};
    const shipAddress = state.order.ship_address || {};

    const { zoneId, ...shipAttrs } = APP_DEFAULTS.toBeDonatedInfo;
    const shipAddressAttributes = ZoneService.isDonationZone() ? shipAttrs : {
        firstname: shipAddress.firstname || '',
        lastname: shipAddress.lastname || '',
        address1: shipAddress.address1 || '',
        address2: shipAddress.address2 || '',
        city: shipAddress.city || defaultShippingCity,
        phone: shipAddress.phone || '',
        zipcode: shipAddress.zipcode || defaultShippingZipCode,
        state_name: shipAddress.state_code || defaultShippingStateCode,
        country_iso: shipAddress.country_iso || defaultShippingCountryIso,
    };
    return {
        billCountryIso,
        billStateName,
        shipCountryIso,
        shipStateName,
        shippingCountriesIds,
        shippingStatesIds,
        shippingCity,
        // defaultBillCountryIso,
        // defaultBillStateName,
        // defaultShippingCountryIso,
        // defaultShippingStateCode,
        enableReinitialize: true,
        initialValues: {
            save_user_address: true,
            order: {
                email: state.order.email || state.user.email,
                email_receiver: state.order.email_receiver,
                receive_offer: true,
                accepts_affidavit_certification: true,
                bill_address_attributes: {
                    firstname: billAddress.firstname || '',
                    lastname: billAddress.lastname || '',
                    address1: billAddress.address1 || '',
                    address2: billAddress.address2 || '',
                    city: billAddress.city || '',
                    phone: billAddress.phone || '',
                    zipcode: billAddress.zipcode || '',
                    state_name: billAddress.state_code || defaultBillStateName,
                    country_iso: billAddress.country_iso || defaultBillCountryIso,
                },
                ship_address_attributes: shipAddressAttributes,
            },
        },
    };
})(AddressForm);

export default injectIntl(AddressForm);



/** WEBPACK FOOTER **
 ** ./src/components/checkout-steps/address-form.jsx
 **/